import { SupabaseClient } from '@supabase/supabase-js'

export interface ContactInquiry {
  id: string
  project_id: string
  user_id: string
  name: string
  contact: string
  message: string
  inquiry_type: 'general' | 'upgrade' | 'support' | 'billing'
  status: 'pending' | 'in_progress' | 'resolved' | 'closed'
  created_at: string
  updated_at: string
}

export interface CreateContactInquiryDto {
  name: string
  contact: string
  message: string
  inquiry_type?: 'general' | 'upgrade' | 'support' | 'billing'
}

export class ContactInquiryService {
  constructor(private supabase: SupabaseClient) {}

  /**
   * Create a new contact inquiry
   */
  async createInquiry(
    projectId: string,
    userId: string,
    dto: CreateContactInquiryDto
  ): Promise<ContactInquiry> {
    const { data, error } = await this.supabase
      .from('contact_inquiries')
      .insert({
        project_id: projectId,
        user_id: userId,
        name: dto.name,
        contact: dto.contact,
        message: dto.message,
        inquiry_type: dto.inquiry_type || 'general'
      })
      .select()
      .single()

    if (error) {
      console.error('Failed to create contact inquiry:', error)
      throw error
    }

    return data
  }

  /**
   * List inquiries for a user
   */
  async listUserInquiries(userId: string): Promise<ContactInquiry[]> {
    const { data, error } = await this.supabase
      .from('contact_inquiries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Failed to list contact inquiries:', error)
      throw error
    }

    return data || []
  }

  /**
   * List inquiries for a project
   */
  async listProjectInquiries(projectId: string): Promise<ContactInquiry[]> {
    const { data, error } = await this.supabase
      .from('contact_inquiries')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Failed to list project inquiries:', error)
      throw error
    }

    return data || []
  }

  /**
   * Get a single inquiry
   */
  async getInquiry(inquiryId: string): Promise<ContactInquiry | null> {
    const { data, error } = await this.supabase
      .from('contact_inquiries')
      .select('*')
      .eq('id', inquiryId)
      .single()

    if (error) {
      console.error('Failed to get inquiry:', error)
      throw error
    }

    return data
  }
}